import { connect } from 'react-redux';
import { css } from 'twin.macro';
import FormSubmissionModal from './FormSubmissionModal';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchWrapper, getRoomIdFromURL } from '@/_helpers';
import { TbTransform } from 'react-icons/tb';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { LoadingSpinner } from '@/_elements/Loaders';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import FormFillWithAudio from '../Forms/FormFillWithAudio';
import { RelativeDiv } from '../Forms/FormFillWithAudio/styles/FormFillWithAudio.styles';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const Component = ({ enableAudioFillBool = true, teamRooms, teams, ...props }) => {
    const roomId = useMemo(() => {
        return (props?.userFormData || {})?.chatroom || getRoomIdFromURL();
    }, [props]);
    const room = useMemo(() => {
        return (
            Object.values(teamRooms || {})
                .flat()
                .flatMap((teamRoom) => teamRoom.rooms || [])
                .find((roomL) => roomL?.id === roomId) || {}
        );
    }, [teamRooms, roomId]);

    const team = useMemo(() => {
        const teamId = room?.team;
        return (teams || []).find((teamL) => teamL?.id === teamId) || {};
    }, [teams, room?.team]);

    const [formData, setFormData] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const hasPageBreak = useMemo(() => {
        if (props?.form) {
            return ((props?.form || {})?.data || [])?.some((field) => field?.key === 'PageBreak');
        }
        return false;
    }, [props?.form]);
    const [transcribedUserFormData, setTranscribedUserFormData] = useState(null);
    const handlePopulateForm = async () => {
        try {
            const alertResponse = await Swal.fire({
                title: `<p style="font-size: 14px;">Do you confirm to fill last submission data for form:</p>
                             <br/> <p style="font-size: 18px;">${props?.form?.name || ''}</p>
                             `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
            });
            if (!alertResponse.isConfirmed) {
                return;
            }
            setIsBusy(true);
            const roomId = props?.currRoom?.id || props?.currRoom?._id;
            const formId = props?.form?._id;
            if (!roomId) {
                throw toast.error('Room id not found');
            }
            if (!formId) {
                throw toast.error('Form id not found');
            }
            const response = await fetchWrapper.get(`forms/?chatroom=${roomId}&populate=true&form=${formId}`);
            if (response?.results && response?.results.length === 1) {
                setFormData(response?.results[0]);
            }
        } catch (e) {
            if (e?.name === 'AbortError') {
                return;
            }
            toast.error(e?.message);
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <Fragment>
            {props?.sharedUserFormDataId || props?.sharedFormId || props?.isPreview ? null : (
                <RelativeDiv>
                    {props?.isDisabled || props?.isSubmitting || (props?.userFormData || {})?._id ? null : (
                        <div
                            css={css`
                                display: flex;
                                justify-content: flex-end;
                                width: 100%;
                                margin-bottom: 8px;
                            `}
                        >
                            <BootstrapToolTip text="Fill last submission data">
                                <Button onClick={handlePopulateForm} disabled={isBusy}>
                                    <TbTransform className={isBusy ? 'spin' : ''} />
                                </Button>
                            </BootstrapToolTip>
                        </div>
                    )}
                    {enableAudioFillBool && (
                        <FormFillWithAudio
                            form={formData || props?.form}
                            roomId={props?.currRoom?.id || props?.currRoom?._id}
                            formId={props?.form?._id}
                            setFormData={setFormData}
                            setIsFetchingUserFormData={setIsBusy}
                            setUserFormData={setTranscribedUserFormData}
                            userFormData={transcribedUserFormData || props?.userFormData}
                        />
                    )}
                </RelativeDiv>
            )}

            {isBusy || hasPageBreak === undefined ? (
                <LoadingSpinner size={124} height={248} />
            ) : (
                <Fragment>
                    <FormSubmissionModal
                        {...props}
                        team={props?.team || team}
                        formType={props?.formType || (formData || {})?.type || (props?.form || {})?.type}
                        form={formData || props?.form}
                        isDisabled={isBusy}
                        handleClose={props?.handleClose || props?.handleCloseModal}
                        userFormData={transcribedUserFormData || props?.userFormData}
                    />
                </Fragment>
            )}
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    user: state.user.user,
    teamRooms: state.rooms.teamRooms,
    teams: state.teams.teams,
});

const FormSubmissionModalWrapper = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, {})(FormSubmissionModalWrapper);
